import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';

export type PushStatus =
  | 'unsupported'        // browser/platform doesn't support Web Push
  | 'needs-install'      // iOS Safari requires Add-to-Home-Screen first
  | 'denied'             // user previously denied permission
  | 'granted-unsubscribed' // permission OK but no active subscription
  | 'subscribed'         // active subscription stored on the server
  | 'unknown';           // initial / loading

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS legacy
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  // Modern
  const mediaStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || mediaStandalone;
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function usePushStatus() {
  const [status, setStatus] = useState<PushStatus>('unknown');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (typeof window === 'undefined') return;
    // iOS Safari doesn't expose PushManager unless the site is installed as a
    // PWA, so we must check this before the generic "unsupported" branch —
    // otherwise iPhone users in regular Safari see a misleading "not supported"
    // message instead of the install prompt.
    if (isIOS() && !isStandalone()) {
      setStatus('needs-install');
      return;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setStatus('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setStatus('denied');
      return;
    }
    // Use getRegistration() — `serviceWorker.ready` hangs forever if no SW is
    // registered yet (the case in dev, where main.tsx skips registration).
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        setStatus('granted-unsubscribed');
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      setStatus(sub ? 'subscribed' : 'granted-unsubscribed');
    } catch (err) {
      setError((err as Error).message);
      setStatus('granted-unsubscribed');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const subscribe = useCallback(async () => {
    setError(null);
    if (!VAPID_PUBLIC_KEY) {
      setError('VAPID public key not configured');
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setStatus(perm === 'denied' ? 'denied' : 'granted-unsubscribed');
        return;
      }
      // Ensure the service worker is registered (skipped in dev by main.tsx).
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        reg = await navigator.serviceWorker.register('/service-worker.js');
      }
      await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const key = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
        const buffer = new ArrayBuffer(key.byteLength);
        new Uint8Array(buffer).set(key);
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: buffer,
        });
      }
      const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error('Subscription missing endpoint or keys');
      }
      const { error: rpcErr } = await supabase.rpc('save_push_subscription', {
        p_endpoint: json.endpoint,
        p_keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        p_user_agent: navigator.userAgent.slice(0, 200),
      });
      if (rpcErr) throw rpcErr;
      setStatus('subscribed');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await supabase.rpc('remove_push_subscription', { p_endpoint: endpoint });
      }
      setStatus('granted-unsubscribed');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  return { status, error, busy, subscribe, unsubscribe, refresh };
}

// Fire-and-forget invoke. Failures are swallowed — the underlying RPC already succeeded.
export async function sendPush(args: {
  userIds: string[];
  title: string;
  body: string;
  url?: string;
  tag?: string;
  allowSelf?: boolean;
}) {
  if (args.userIds.length === 0) return;
  try {
    await supabase.functions.invoke('send-push', {
      body: {
        user_ids: args.userIds,
        title: args.title,
        body: args.body,
        url: args.url,
        tag: args.tag,
        allow_self: args.allowSelf,
      },
    });
  } catch {
    /* fire-and-forget */
  }
}

// Self-notify smoke test. Throws on failure so the caller can show the error.
export async function sendTestPushToSelf(userId: string) {
  const { data, error } = await supabase.functions.invoke('send-push', {
    body: {
      user_ids: [userId],
      title: 'Patch Legacy test ✓',
      body: "If you can read this, push is wired up end-to-end on this device.",
      url: '/profile',
      tag: 'self-test',
      allow_self: true,
    },
  });
  if (error) throw error;
  return data as { sent: number; total: number };
}
